import { utils } from "@omegagrid/core";
import { GridAdapter, RequestFilters, FilterRange, parseNumericExpression, isSingleNumber } from "@omegagrid/grid";
import { RequestParams, RequestParamsOrder, RequestParamsWhere, RequestParamsWhereCondition, RequestParamsWhereExpr } from "../types";
import { formatDateTime } from "../formatters";

export abstract class BaseGridAdapter extends GridAdapter {

	/**
	 * Default logic of transformation of grid filters to request params
	 * 
	 * @param filters filters from grid
	 * @param defaults default request params
	 * @param colNameReplacement optional mapping of column names from grid to request params (if they differ) in format {gridColName: requestParamColName}
	 * @returns transformed request params
	 */
	createRequestParams(filters?: RequestFilters, defaults?: RequestParams, colNameReplacement?: Record<string, string>) {
		const params: RequestParams = utils.cloneDeep(defaults || {});
		if (!filters) return params;
		
		// the request that loads the model carries the columns its indexes refer to, the later ones refer to the model
		const columns = filters.columns
			? filters.columns.map<{data: string, type: string}>(col => ({ data: col.data?.toString(), type: col.t }))
			: this.component.model?.columns?.map<{data: string, type: string}>(col => ({ data: col.data?.toString(), type: col.type }));

		if (columns && colNameReplacement) { // Replace column names
			for (const col of columns) {
				if (colNameReplacement[col.data]) {
					col.data = colNameReplacement[col.data];
				}
			}
		}

		if (columns && filters.order?.length > 0) {
			params.order = filters.order.map(item => [
				columns[item.c].data,
				item.ord >= 0 ? 'asc' : 'desc'
			]) as RequestParamsOrder;
		}

		const where: Record<string, RequestParamsWhereExpr|number|string|unknown[]> = {};
		if (columns && filters?.filter) {
			filters.filter.forEach(item => {
				const column = columns[item.c];
				const colName = column.data;
				if (this.isFilterRange(item.term)) {
					const range = this.serializeRange(item.term, column.type);
					if (range) where[colName] = range;
				} else if (Array.isArray(item.term)) {
					if (item.term.length === 0) return;
					where[colName] = item.term.map(v => v.key);
				} else if (column.type === 'b') {
					where[colName] = (!!item.term) ? "1" : "0";
				} else if (column.type === 'n') {
					const expr = this.serializeNumericExpression(item.term);
					if (expr != null) where[colName] = expr;
				} else {
					where[colName] = `~${item.term}~`;
				}
			});
		}

		if (Object.keys(where).length > 0) {
			params.where = where as RequestParamsWhere;
		}

		params.offset = filters?.offset || 0;
		return params;
	}

	private isFilterRange(term: unknown): term is FilterRange {
		return !!term && typeof term === 'object' && !Array.isArray(term)
			&& (term as FilterRange).kind === 'range';
	}

	/**
	 * Serialize a numeric text-filter term into request params. A lone bare number is sent
	 * as a text "contains" match (matching the grid's client-side behaviour); anything with
	 * comparison operators (`>0`, `10..20`, `<10 or >20`, `1 2 3`, ...) becomes a disjunction
	 * of conjunctions `{ or: [[{op, value}, ...], ...] }`. Returns null when there is nothing
	 * meaningful to filter by.
	 */
	private serializeNumericExpression(term: unknown): RequestParamsWhereExpr | string | null {
		const text = (term ?? '').toString().trim();
		if (text === '') return null;
		if (isSingleNumber(text)) return `~${text}~`;
		const groups = parseNumericExpression(text);
		if (groups.length === 0) return `~${text}~`;
		return { or: groups };
	}

	private serializeRange(term: FilterRange, type: string): RequestParamsWhereExpr | null {
		const from = this.serializeRangeValue(term.from, type);
		const to = this.serializeRangeValue(term.to, type, true);
		if (from == null && to == null) return null;
		const conditions: RequestParamsWhereCondition[] = [];
		if (from != null) conditions.push({ op: '>=', value: from });
		if (to != null) conditions.push({ op: '<=', value: to });
		return { or: [conditions] };
	}

	private serializeRangeValue(v: unknown, type: string, isTo?: boolean): number | string | null {
		if (v == null || v === '') return null;
		if (v instanceof Date) {
			const dt = formatDateTime(v, 'yyyy-MM-dd HH:mm:ss');
			const d = dt.split(' ')[0];
			const t = dt.split(' ')[1];
			if (type === 'd' || type === 'dt') return `${d} ${isTo ? '23:59:59.999' : '00:00:00.000'}`; // sql format
			if (type === 'tm') return t;
			return dt;
		}
		if (typeof v === 'number') return v;
		return String(v);
	}

}
